import { useState } from 'react';
import { XMarkIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { supabase } from '../services/supabaseClient';
import { PRICING } from '../config/pricing';

interface AddEnvironmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentEnvironmentCount: number;
  billingInterval: 'month' | 'year';
}

interface CouponDetails {
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: string;
  durationInMonths: number | null;
  name: string | null;
}

export default function AddEnvironmentModal({
  isOpen,
  onClose,
  onSuccess,
  currentEnvironmentCount,
  billingInterval
}: AddEnvironmentModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponDetails, setCouponDetails] = useState<CouponDetails | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);

  const pricePerEnvironment = billingInterval === 'year' ? PRICING.ADD_ONS.ENVIRONMENT_ANNUAL : PRICING.ADD_ONS.ENVIRONMENT_MONTHLY;
  const totalPrice = pricePerEnvironment * quantity;
  const priceDisplay = billingInterval === 'year' ? `/year` : `/month`;

  // Calculate discounted price
  let discountedPrice = totalPrice;
  let discountAmount = 0;
  if (couponDetails) {
    if (couponDetails.percentOff) {
      discountAmount = (totalPrice * couponDetails.percentOff) / 100;
      discountedPrice = totalPrice - discountAmount;
    } else if (couponDetails.amountOff) {
      // Convert cents to dollars if needed
      const amountOffDollars = couponDetails.currency === 'usd' ? couponDetails.amountOff / 100 : couponDetails.amountOff;
      discountAmount = amountOffDollars;
      discountedPrice = Math.max(0, totalPrice - discountAmount);
    }
  }

  const validateCoupon = async (code: string) => {
    if (!code.trim()) {
      setCouponDetails(null);
      setCouponError(null);
      return;
    }

    setIsValidating(true);
    setCouponError(null);

    try {
      const response = await fetch('/api/subscription/validate-coupon', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ couponCode: code.trim() })
      });

      const data = await response.json();

      if (data.valid) {
        setCouponDetails(data.coupon);
        setCouponError(null);
      } else {
        setCouponDetails(null);
        setCouponError(data.error || 'Invalid coupon code');
      }
    } catch (err) {
      console.error('Error validating coupon:', err);
      setCouponDetails(null);
      setCouponError('Failed to validate coupon');
    } finally {
      setIsValidating(false);
    }
  };

  const handleCouponChange = (value: string) => {
    setCouponCode(value);
    // Clear previous validation
    setCouponDetails(null);
    setCouponError(null);
  };

  const handleCouponBlur = () => {
    if (couponCode.trim()) {
      validateCoupon(couponCode);
    }
  };

  const handleAddEnvironment = async () => {
    setIsAdding(true);
    setError(null);

    try {
      const response = await fetch('/api/subscription/add-environment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          quantity,
          couponCode: couponCode.trim() || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add environment');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error adding environment:', err);
      setError(err.message || 'Failed to add environment');
    } finally {
      setIsAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-lg w-full shadow-xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Add Environments</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-500">
            Each environment allows you to connect one production Salesforce org and up to 4 of its sandboxes.
          </p>

          {/* Quantity Selection */}
          <div>
            <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
              Number of Environments
            </label>
            <input
              type="number"
              id="quantity"
              min="1"
              max="50"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          {/* Coupon Code */}
          <div>
            <label htmlFor="couponCode" className="block text-sm font-medium text-gray-700 mb-2">
              Coupon Code (Optional)
            </label>
            <input
              type="text"
              id="couponCode"
              value={couponCode}
              onChange={(e) => handleCouponChange(e.target.value)}
              onBlur={handleCouponBlur}
              placeholder="Enter coupon code"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
            />
            {isValidating && (
              <p className="mt-1 text-sm text-gray-500">Validating coupon...</p>
            )}
            {couponDetails && !isValidating && (
              <p className="mt-1 text-sm text-green-600">
                ✓ {couponDetails.percentOff ? `${couponDetails.percentOff}% off` : `$${(couponDetails.amountOff! / 100).toFixed(2)} off`}
                {couponDetails.duration === 'repeating' && couponDetails.durationInMonths &&
                  ` for ${couponDetails.durationInMonths} months`}
                {couponDetails.duration === 'forever' && ` forever`}
              </p>
            )}
            {couponError && !isValidating && (
              <p className="mt-1 text-sm text-red-600">✗ {couponError}</p>
            )}
          </div>

          {/* Summary */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Current environments:</span>
              <span className="text-sm text-gray-900">{currentEnvironmentCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">After adding:</span>
              <span className="text-sm text-gray-900">{currentEnvironmentCount + quantity}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600">
                  ${pricePerEnvironment}{priceDisplay} × {quantity} {quantity === 1 ? 'environment' : 'environments'}
                </span>
                <span className={`text-sm ${couponDetails ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                  ${totalPrice.toLocaleString()}{priceDisplay}
                </span>
              </div>
              {couponDetails && (
                <>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-green-600">
                      Discount ({couponDetails.percentOff ? `${couponDetails.percentOff}%` : `$${(couponDetails.amountOff! / 100).toFixed(2)}`}):
                    </span>
                    <span className="text-sm text-green-600">-${discountAmount.toLocaleString()}{priceDisplay}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-sm font-semibold text-gray-900">Total after discount:</span>
                    <span className="text-lg font-bold text-green-600">
                      ${discountedPrice.toLocaleString()}{priceDisplay}
                    </span>
                  </div>
                </>
              )}
              {!couponDetails && (
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-900">Additional cost:</span>
                  <span className="text-lg font-bold text-gray-900">
                    ${totalPrice.toLocaleString()}{priceDisplay}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Next step:</strong> After adding {quantity === 1 ? 'this environment' : 'these environments'}, you'll be able to connect new production orgs from the Organizations page.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 p-3 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isAdding}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleAddEnvironment}
            disabled={isAdding}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAdding ? 'Adding...' : `Add ${quantity} ${quantity === 1 ? 'Environment' : 'Environments'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
